import { useEffect, useRef, useState } from "react";

const BASE_CPS = 40;
const MAX_CPS = 150;
const CATCHUP_SECONDS = 1.5;
// Upper bound on how much backlog the typewriter will ever animate. During
// live streaming the adaptive catch-up keeps the reveal within ~CATCHUP_SECONDS
// of the target (≈ a couple hundred chars), so this is unreachable while the
// user is watching. It only kicks in when the reveal was paused — hidden tab,
// frozen iframe — and a large backlog piled up: instead of replaying the whole
// reply "from the beginning", the cursor jumps so that at most
// MAX_LAG_CHARS / MAX_CPS (≈4s) of text animates.
const MAX_LAG_CHARS = 600;

/**
 * Animate a moving target string char-by-char via requestAnimationFrame.
 *
 * Reveal speed is adaptive: a small lag reveals at BASE_CPS, a large backlog
 * catches up so the display is at most CATCHUP_SECONDS behind the target.
 * When `isStreaming` becomes false, any remaining chars are flushed.
 */
export function useSmoothStream(
  targetText: string | undefined,
  isStreaming: boolean
): string {
  // If we mount with content already present and no streaming in progress, this
  // is historical/loaded content — render it instantly with no animation.
  const initialInstantRef = useRef<boolean>(
    !!targetText && targetText.length > 0 && !isStreaming
  );
  const [rendered, setRendered] = useState<string>(
    initialInstantRef.current ? (targetText ?? "") : ""
  );

  const targetRef = useRef<string>(initialInstantRef.current ? (targetText ?? "") : "");
  const streamingRef = useRef<boolean>(isStreaming);
  const cursorRef = useRef(initialInstantRef.current ? (targetText?.length ?? 0) : 0);
  const rafRef = useRef<number | null>(null);
  const lastTimeRef = useRef<number | null>(null);

  useEffect(() => {
    streamingRef.current = isStreaming;
  }, [isStreaming]);

  useEffect(() => {
    const target = targetText ?? "";

    // Empty input or target shrank (new message) → reset.
    if (!target) {
      cursorRef.current = 0;
      targetRef.current = "";
      lastTimeRef.current = null;
      if (rafRef.current != null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      setRendered("");
      return;
    }
    // Handle the target changing in ways other than pure append.
    const cursor = cursorRef.current;
    const prev = targetRef.current;
    const changed =
      cursor > 0 &&
      (target.length < prev.length ||
        target.slice(0, cursor) !== prev.slice(0, cursor));
    if (changed) {
      // Distinguish a genuine message swap (e.g. status "Thinking…" replaced
      // by the real reply — nothing in common) from an in-place edit of
      // already-revealed text (e.g. a source citation injected into an
      // earlier sentence — long shared prefix). A swap restarts the reveal;
      // an edit keeps the cursor where it is so the reveal continues
      // seamlessly instead of re-typing the whole answer.
      let common = 0;
      const max = Math.min(prev.length, target.length);
      while (common < max && prev[common] === target[common]) common++;
      if (common >= 24) {
        cursorRef.current = Math.min(cursor, target.length);
        setRendered(target.slice(0, cursorRef.current));
      } else {
        cursorRef.current = 0;
        setRendered("");
      }
    }
    targetRef.current = target;

    const tick = (now: number) => {
      const last = lastTimeRef.current ?? now;
      const dt = Math.max(0, (now - last) / 1000);
      lastTimeRef.current = now;

      const t = targetRef.current;
      let lag = t.length - cursorRef.current;
      if (lag <= 0) {
        rafRef.current = null;
        lastTimeRef.current = null;
        return;
      }

      // Bound the replay after a paused reveal (see MAX_LAG_CHARS).
      if (lag > MAX_LAG_CHARS) {
        cursorRef.current = t.length - MAX_LAG_CHARS;
        lag = MAX_LAG_CHARS;
      }

      let cps: number;
      if (!streamingRef.current) {
        cps = MAX_CPS;
      } else {
        const catchupCps = lag / CATCHUP_SECONDS;
        cps = Math.min(MAX_CPS, Math.max(BASE_CPS, catchupCps));
      }

      const advance = Math.max(1, Math.floor(cps * dt));
      cursorRef.current = Math.min(t.length, cursorRef.current + advance);
      setRendered(t.slice(0, cursorRef.current));

      if (cursorRef.current < t.length) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        rafRef.current = null;
        lastTimeRef.current = null;
      }
    };

    if (rafRef.current == null && cursorRef.current < target.length) {
      rafRef.current = requestAnimationFrame(tick);
    }
  }, [targetText]);

  // When stream completes, kick the loop so flush happens even if no further
  // targetText update arrives.
  useEffect(() => {
    if (!isStreaming && cursorRef.current < targetRef.current.length) {
      if (rafRef.current == null) {
        const tick = (now: number) => {
          const last = lastTimeRef.current ?? now;
          const dt = Math.max(0, (now - last) / 1000);
          lastTimeRef.current = now;
          const t = targetRef.current;
          let lag = t.length - cursorRef.current;
          if (lag <= 0) {
            rafRef.current = null;
            lastTimeRef.current = null;
            return;
          }
          // Bound the replay after a paused reveal (see MAX_LAG_CHARS).
          if (lag > MAX_LAG_CHARS) {
            cursorRef.current = t.length - MAX_LAG_CHARS;
            lag = MAX_LAG_CHARS;
          }
          const advance = Math.max(1, Math.floor(MAX_CPS * dt));
          cursorRef.current = Math.min(t.length, cursorRef.current + advance);
          setRendered(t.slice(0, cursorRef.current));
          if (cursorRef.current < t.length) {
            rafRef.current = requestAnimationFrame(tick);
          } else {
            rafRef.current = null;
            lastTimeRef.current = null;
          }
        };
        rafRef.current = requestAnimationFrame(tick);
      }
    }
  }, [isStreaming]);

  useEffect(() => {
    return () => {
      if (rafRef.current != null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, []);

  return rendered;
}
